import classNames from "classnames";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Cross } from "components/icons/Cross";
import { Trash } from "components/icons/Trash";
import { Form, Formik, useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ComponentType, ReactNode, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import GrayPencil from "static/images/GrayPencil.svg";
import RedInfo from "static/images/RedFilledInfo.svg";
import { autofillApi, useGetAutofillQuery } from "store/apis/autofill";
import { useUpdateTaskFormDataMutation } from "store/apis/task";
import { AddressAutofill } from "types/Models/addressAutofill";
import { BankAutofill } from "types/Models/bankAutofill";
import {
  AutofillDetailKey,
  FilingFormType,
  RepeatableField,
  Section,
  SectionField,
} from "types/Models/Filing";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { BackendError } from "types/utils/error";
import { DeleteFilingAutofill } from "./DeleteFilingAutofill";
import { FilingInfo } from "./FilingReviewSectionDisplay";
import { SelectedAutofillDetails } from "./SelectedAutofillDetails";
import { AddressComponents } from "components/AddressComponent/AddressComponent";
import ToolTip from "components/design/toolTip";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import { formatDate } from "utils/formatDate";
import { DELAWARE_AGENT_ADDRESS_ERROR } from "formValidations/filingFormSchema";
import { findAutofillKey, mapSectionWithFormValues } from "./autofillUtils";

export type SelectAutofillProps = {
  section: Section;
  groupId: string;
  entityId: string;
  formData: FilingFormType;
  autofillKey: string;
  field: SectionField;
  keysToFill: string[];
  deleteModalTitle: string;
  addNewAutofillText: string;
  comboboxName: string;
  currentAutofillDetailKey: string;
  shouldShowDetailsCard: boolean;
  isAnyDetailsMissing: boolean;
  AutofillCreateEdit: ComponentType<any>;
  autofillSuffix?: string;
  repeatableFieldData: RepeatableField;
  fields: SectionField[];
  label?: string | ReactNode;
  error?: string;
};

export const SelectAutofill = ({
  section,
  groupId,
  entityId,
  formData,
  autofillKey,
  field,
  keysToFill,
  deleteModalTitle,
  addNewAutofillText,
  comboboxName,
  currentAutofillDetailKey,
  shouldShowDetailsCard,
  isAnyDetailsMissing,
  AutofillCreateEdit,
  autofillSuffix,
  repeatableFieldData,
  fields,
  label,
  error,
}: SelectAutofillProps) => {
  const { data: autofills = [], isLoading } = useGetAutofillQuery(
    {
      groupId,
      autofillKey,
      entityId,
    },
    { skip: !groupId || !entityId }
  );
  const [isCreatingManually, setCreatingManually] = useState(false);

  const {
    isOpen: showAddModal,
    close: closeAddModal,
    open: openAddModal,
  } = useModal();
  const {
    isOpen: showDeleteModal,
    close: closeDeleteModal,
    open: openDeleteModal,
  } = useModal();
  const {
    isOpen: showEditModal,
    close: closeEditModal,
    open: openEditModal,
  } = useModal();

  const { values: formValues } = useFormikContext<Record<string, any>>();
  const [updateTaskForm, { isLoading: isUpdatingTask }] =
    useUpdateTaskFormDataMutation();
  const { taskId } = useParams<{ taskId: string }>();
  const { alertToast, successToast } = useToast();
  const [detailsForEdit, setDetailsForEdit] = useState<
    BankAutofill | AddressAutofill | PersonaAutofill | undefined
  >();
  const dispatch = useDispatch();

  const currentSelectedAutofillDetails = autofillSuffix
    ? repeatableFieldData?.selected_autofill_details?.[
        currentAutofillDetailKey as AutofillDetailKey
      ]
    : section.selected_autofill_details?.[
        currentAutofillDetailKey as AutofillDetailKey
      ];

  const getUpdatedField = (
    fieldKey: string,
    field: SectionField,
    values: any
  ) => {
    let keyToFill = findAutofillKey(keysToFill, fieldKey);

    if (keyToFill && keyToFill === "bank_brand_name") {
      return { ...field, value: values.name || "" };
    }

    if (keyToFill && keyToFill.includes("select_autofill")) {
      return { ...field, value: values.uuid };
    }
    if (fieldKey === "company_state_of_incorporation") {
      return { ...field, value: formValues?.[fieldKey] ?? field.value };
    }

    if (keyToFill) {
      return { ...field, value: values[keyToFill] };
    }

    //@ts-ignore
    return { ...field, value: formValues?.[fieldKey] || field.value };
  };

  const getRepeatableUpdatedField = (
    fieldKey: string,
    field: SectionField,
    values: any
  ) => {
    let keysToFillWithoutSuffix = keysToFill.map((autofillKey) =>
      autofillKey?.replace(`_${autofillSuffix}`, "")
    );
    let keyToFill = findAutofillKey(keysToFillWithoutSuffix, fieldKey);
    if (keyToFill && keyToFill === "bank_brand_name") {
      return { ...field, value: values.name || "" };
    }

    if (keyToFill && keyToFill.includes("select_autofill")) {
      return { ...field, value: values.uuid };
    }

    if (keyToFill) {
      return { ...field, value: values[keyToFill] };
    }
    //@ts-ignore
    return { ...field, value: formValues?.[fieldKey] || field.value };
  };

  const updateAutofillSection = (updatedFieldsWithValue: {
    section_fields: {}[];
  }) => {
    return formData?.data?.data?.section_group?.[0].sections.map(
      (currentSection: { [x: string]: any | {}[]; section_key: string }) => {
        if (currentSection.section_key === section.section_key) {
          return updatedFieldsWithValue;
        }
        return mapSectionWithFormValues(currentSection, formValues);
      }
    );
  };

  const getPayload = (updatedSections: any) => {
    const payload = {
      form_data: {
        ...formData,
        data: {
          ...formData.data.data,
          section_group: [{ sections: updatedSections }],
        },
      },
    };
    return payload;
  };

  const onComplete = async () => {
    try {
      dispatch(autofillApi.util.invalidateTags(["AUTOFILL"]));
      closeAddModal();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const updateTaskSection = async (
    autofillValue: SingleValue<OptionData> | MultiValue<OptionData> = []
  ) => {
    try {
      const selectedAutofill = (autofillValue as SingleValue<OptionData>)
        ?.value;

      if (selectedAutofill) {
        const valuesToSet = autofills.find(
          ({ uuid }) => uuid === selectedAutofill
        );
        await updateTaskDetails(valuesToSet!);
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const updateTaskSectionAddress = async (autofill: AddressAutofill | null) => {
    try {
      if (autofill) {
        await updateTaskDetails(autofill!);
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleDelete = async () => {
    try {
      const valuesToSet = keysToFill.map((key) => ({ [key]: "" }));
      await updateTaskDetails(valuesToSet, {});
      closeDeleteModal();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
      closeDeleteModal();
    }
  };

  const updateTaskDetails = async (
    autofill:
      | BankAutofill
      | AddressAutofill
      | PersonaAutofill
      | { [x: string]: string }[],
    selectedAutofillDetails?: any
  ) => {
    try {
      const sectionWithFormValues = mapSectionWithFormValues(
        section,
        formValues
      );
      let updatedFieldWithValue: any = {};

      // repeatable field logic
      if (autofillSuffix) {
        updatedFieldWithValue = {
          ...sectionWithFormValues,
          repeatable_fields: sectionWithFormValues.repeatable_fields?.map(
            (repeatableField) => {
              if (repeatableField.id != Number(autofillSuffix)) {
                return repeatableField;
              }
              return {
                ...repeatableField,
                fields: repeatableField?.fields?.map((field: SectionField) => {
                  return getRepeatableUpdatedField(
                    field.field_key,
                    field,
                    autofill
                  );
                }),
                selected_autofill_details: {
                  ...repeatableFieldData.selected_autofill_details,
                  [currentAutofillDetailKey]:
                    selectedAutofillDetails || autofill,
                },
              };
            }
          ),
        };
      } else {
        updatedFieldWithValue = {
          ...sectionWithFormValues,
          selected_autofill_details: {
            ...section.selected_autofill_details,
            [currentAutofillDetailKey]: selectedAutofillDetails || autofill,
          },
          section_fields: sectionWithFormValues.section_fields?.map(
            (field: SectionField) => {
              return getUpdatedField(field.field_key, field, autofill);
            }
          ),
        };
      }
      const updatedSections = updateAutofillSection(updatedFieldWithValue);
      const payload = getPayload(updatedSections);

      await updateTaskForm({
        formId: formData.uuid,
        payload,
        taskId,
      }).unwrap();
      successToast({ message: "Task updated successfully!" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const openEdit = (
    autofillDetails: BankAutofill | AddressAutofill | PersonaAutofill
  ) => {
    setDetailsForEdit(autofillDetails);
    openEditModal();
  };

  const closeEdit = () => {
    setDetailsForEdit(undefined);
    closeEditModal();
  };

  const onClose = () => {
    closeAddModal();
    closeEdit();
  };

  const defaultSelected = currentSelectedAutofillDetails?.autofill_string
    ? {
        label: (
          <div className="t-text-body-sm t-w-full t-truncate">
            {currentSelectedAutofillDetails?.autofill_string}
          </div>
        ),
        value: currentSelectedAutofillDetails?.uuid!,
      }
    : null;

  const fieldPrefix = field.autofill_prefix + "_";

  if (isLoading) {
    return <Loader />;
  }

  if (autofillKey === "addresses") {
    return (
      <AddressComponents
        formPrefix=""
        entityId={entityId}
        groupId={groupId}
        selectedAddressId={currentSelectedAutofillDetails?.uuid}
        label={label || "Address"}
        onAddressSelect={updateTaskSectionAddress}
        hideClear
        onClearAddress={() => setCreatingManually(false)}
        handleCreateManually={() => setCreatingManually(true)}
        fieldPrefix={fieldPrefix}
        note={error === DELAWARE_AGENT_ADDRESS_ERROR ? undefined : field.note}
        error={error}
      />
    );
  }

  return (
    <>
      <div>
        <Combobox
          actions={
            <Button type="button" customType="link" onClick={openAddModal}>
              {addNewAutofillText}
            </Button>
          }
          components={{ ClearIndicator: () => null }}
          onChange={updateTaskSection}
          value={defaultSelected}
          withForm
          name={comboboxName}
          label={field.field_label}
          placeholder={field.placeholder}
          options={autofills.map((autofill) => {
            return {
              label: (
                <div className="t-flex t-justify-between t-items-center t-w-full">
                  <div className="t-text-body-sm t-w-full t-truncate">
                    {autofill.autofill_string}
                  </div>
                  {autofill.current_balance !== null &&
                    autofill.current_balance !== undefined && (
                      <div className="t-flex t-items-center t-gap-1">
                        <div className="t-text-body-sm t-text-text-30">
                          ${autofill.current_balance?.toFixed(2)}
                        </div>
                        {autofill.balance_as_on_date && (
                          <ToolTip
                            text={`Balance as of ${formatDate(
                              autofill.balance_as_on_date
                            )}`}
                          >
                            <img
                              src={FilingInfoIcon}
                              alt="FilingInfoIcon"
                              height="12"
                            />
                          </ToolTip>
                        )}
                      </div>
                    )}
                </div>
              ),
              value: autofill.uuid,
            };
          })}
          isLoading={isUpdatingTask}
          tooltipText={field.tooltip}
        />

        {shouldShowDetailsCard && (
          <div className="t-mb-3 t-mt-6">
            <div
              className={classNames(
                "t-border t-border-solid t-border-neutral-0 t-p-4 t-rounded-lg",
                {
                  "t-border-red-30": isAnyDetailsMissing,
                }
              )}
            >
              <div className="t-flex t-justify-between">
                <div className="t-grid t-gap-x-20 t-gap-y-4 t-grid-cols-2 t-w-4/5">
                  {keysToFill
                    .filter((keyToFill) => !keyToFill.includes("autofill"))
                    ?.map((keyToFill, index) => {
                      const fieldData = autofillSuffix
                        ? repeatableFieldData?.fields?.find(
                            (field: SectionField) =>
                              field.field_key?.includes(keyToFill)
                          )
                        : fields?.find((field: SectionField) =>
                            field.field_key?.includes(keyToFill)
                          );

                      if (
                        !fieldData ||
                        fieldData?.type?.includes("autofill") ||
                        fieldData?.field_key?.includes("last_name")
                      ) {
                        return null;
                      }

                      if (fieldData?.field_key?.includes("first_name")) {
                        const lastName = autofillSuffix
                          ? repeatableFieldData?.fields?.find(
                              (field: SectionField) =>
                                field.field_key?.includes("last_name")
                            )?.value
                          : fields?.find((field: SectionField) =>
                              field.field_key?.includes("last_name")
                            )?.value;

                        return (
                          <FilingInfo
                            key={index}
                            label="Full Name"
                            value={fieldData.value + " " + lastName}
                          />
                        );
                      }

                      return (
                        <FilingInfo
                          key={index}
                          label={fieldData.field_label}
                          value={fieldData.value}
                        />
                      );
                    })}
                </div>
                <div className="t-flex t-gap-2">
                  <Button
                    onClick={() => openEdit(currentSelectedAutofillDetails!)}
                    customType="ghost"
                    size="small"
                    type="button"
                  >
                    <img
                      src={GrayPencil}
                      className="t-h-4 t-m-1"
                      alt="GrayPencil"
                    />
                  </Button>
                  <Button
                    onClick={openDeleteModal}
                    customType="ghost"
                    size="small"
                    type="button"
                  >
                    <span className="t-text-text-30">
                      <Cross />
                    </span>
                  </Button>
                </div>
              </div>
            </div>

            {isAnyDetailsMissing && !isCreatingManually && (
              <div className="t-text-red t-text-body-sm t-mt-1 t-flex t-items-center t-gap-1">
                <img src={RedInfo} alt="RedInfo" />
                <div>
                  Please update missing information. Edit to complete the
                  required fields
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <DeleteFilingAutofill
        isOpen={showDeleteModal}
        close={closeDeleteModal}
        handleDeleteBank={handleDelete}
        isLoading={isUpdatingTask}
        title={deleteModalTitle}
      />
      <AutofillCreateEdit
        isOpenAdd={showAddModal}
        isOpenEdit={showEditModal}
        onClose={onClose}
        entityId={entityId}
        onSuccess={updateTaskDetails}
        groupId={groupId}
        onComplete={onComplete}
        isEditFlow={showEditModal}
        autofillKey={autofillKey}
        //@ts-ignore
        initialValue={detailsForEdit}
      />
    </>
  );
};
